import { useMemo, useState } from 'react';
import { Eye, Megaphone, Plus, Save, Trash2 } from 'lucide-react';
import { AnnouncementDialog } from '@forq/ui';
import type {
  AdminAnnouncementRecord,
  AdminSnapshot,
  AnnouncementRepeat,
  AnnouncementTone,
  SaveAnnouncementInput,
} from '../types';

interface AnnouncementsFeatureProps {
  readonly snapshot: AdminSnapshot;
  readonly canManage: boolean;
  readonly busy: boolean;
  readonly onSave: (input: Omit<SaveAnnouncementInput, 'tenantId'>) => Promise<void>;
  readonly onDelete: (announcementId: string) => Promise<void>;
}

interface DraftState {
  readonly id: string | null;
  readonly title: string;
  readonly body: string;
  readonly tone: AnnouncementTone;
  readonly repeat: AnnouncementRepeat;
  readonly active: boolean;
}

const emptyDraft: DraftState = {
  id: null,
  title: '',
  body: '',
  tone: 'info',
  repeat: 'once',
  active: false,
};

const toneOptions: readonly { readonly value: AnnouncementTone; readonly label: string }[] = [
  { value: 'info', label: 'Information (blau)' },
  { value: 'warning', label: 'Wichtiger Hinweis (gelb)' },
  { value: 'critical', label: 'Achtung (rot)' },
];

const repeatOptions: readonly {
  readonly value: AnnouncementRepeat;
  readonly label: string;
  readonly hint: string;
}[] = [
  {
    value: 'once',
    label: 'Einmal, bis jeder sie weggeklickt hat',
    hint: 'Für Nachrichten wie „Changelog V1.1". Wer sie gelesen hat, sieht sie nicht wieder – änderst du den Text, kommt sie erneut.',
  },
  {
    value: 'every-start',
    label: 'Bei jedem Start',
    hint: 'Für stehende Warnungen wie „Achtung Beta-Phase!". Sparsam einsetzen: was immer kommt, klickt man irgendwann ungelesen weg.',
  },
];

/** Die Datenbank prueft dasselbe (CHECK-Constraints). Hier nur frueher, damit es nicht knallt. */
const titleLimit = 120;
const bodyLimit = 2000;

function draftFrom(record: AdminAnnouncementRecord): DraftState {
  return {
    id: record.id,
    title: record.title,
    body: record.body,
    tone: record.tone,
    repeat: record.repeat,
    active: record.active,
  };
}

function dateLabel(value: string): string {
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function problemWith(draft: DraftState): string | null {
  if (draft.title.trim().length < 2) return 'Die Überschrift fehlt.';
  if (draft.title.length > titleLimit) return `Die Überschrift ist zu lang (max. ${titleLimit} Zeichen).`;
  if (draft.body.trim().length < 2) return 'Der Text fehlt.';
  if (draft.body.length > bodyLimit) return `Der Text ist zu lang (max. ${bodyLimit} Zeichen).`;
  return null;
}

/**
 * Systemmeldungen: das Fenster, das die Kollegen beim Start von FORQ sehen.
 *
 * Je Mandant ist hoechstens EINE Meldung aktiv -- zwei Fenster hintereinander liest niemand.
 * Aktivieren schaltet die bisherige deshalb sichtbar ab, statt sie danebenzustellen.
 *
 * Der Aufrufer gibt einen `key` je Mandant mit: beim Wechsel entsteht die Komponente neu und der
 * Entwurf ist weg. Sonst traegt man FTWs Beta-Warnung ein, wechselt zu Spezialtransporte und
 * speichert sie dort.
 */
export function AnnouncementsFeature({
  snapshot,
  canManage,
  busy,
  onSave,
  onDelete,
}: AnnouncementsFeatureProps) {
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [error, setError] = useState<string | null>(null);

  const active = useMemo(
    () => snapshot.announcements.find((entry) => entry.active) ?? null,
    [snapshot.announcements],
  );

  const problem = problemWith(draft);
  const editing = draft.id !== null;
  // Bewusst nicht draft.active: die Vorschau zeigt, wie DIESE Meldung aussieht, nicht ob sie laeuft.
  const preview = draft.title.trim() || draft.body.trim() ? draft : null;

  async function save() {
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    try {
      await onSave({
        id: draft.id,
        title: draft.title.trim(),
        body: draft.body.trim(),
        tone: draft.tone,
        repeat: draft.repeat,
        active: draft.active,
      });
      setDraft(emptyDraft);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Die Systemmeldung konnte nicht gespeichert werden.');
    }
  }

  return (
    <section className="admin-announce">
      <header className="admin-announce__head">
        <div>
          <h2>Systemmeldungen</h2>
          <p>
            Das Fenster, das den Kolleginnen und Kollegen beim Start von FORQ angezeigt wird –
            zum Beispiel ein Beta-Hinweis oder ein Changelog. Gilt nur für diesen Mandanten.
          </p>
        </div>
        <span className={`admin-announce__state${active ? ' is-live' : ''}`}>
          <Megaphone size={14} /> {active ? 'Eine Meldung ist aktiv' : 'Keine Meldung aktiv'}
        </span>
      </header>

      {canManage ? (
        <div className="admin-announce__grid">
          <form className="admin-announce__form" onSubmit={(event) => { event.preventDefault(); void save(); }}>
            <h3>{editing ? 'Meldung bearbeiten' : 'Neue Meldung'}</h3>

            <label>
              Überschrift
              <input
                value={draft.title}
                maxLength={titleLimit}
                placeholder="Achtung Beta-Phase!"
                onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
              />
            </label>

            <label>
              Text
              <textarea
                value={draft.body}
                rows={5}
                maxLength={bodyLimit}
                placeholder={'FORQ ist noch im Aufbau.\n\nBitte meldet Fehler über das Reportingtool.'}
                onChange={(event) => setDraft((current) => ({ ...current, body: event.target.value }))}
              />
              <small className="admin-announce__count">{draft.body.length} / {bodyLimit} Zeichen</small>
            </label>

            <label>
              Ton
              <select
                value={draft.tone}
                onChange={(event) => setDraft((current) => ({ ...current, tone: event.target.value as AnnouncementTone }))}
              >
                {toneOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <fieldset className="admin-announce__repeat">
              <legend>Wie oft?</legend>
              {repeatOptions.map((option) => (
                <label key={option.value} className="admin-announce__radio">
                  <input
                    type="radio"
                    name="repeat"
                    checked={draft.repeat === option.value}
                    onChange={() => setDraft((current) => ({ ...current, repeat: option.value }))}
                  />
                  <span>
                    <strong>{option.label}</strong>
                    <small>{option.hint}</small>
                  </span>
                </label>
              ))}
            </fieldset>

            <label className="admin-announce__switch">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(event) => setDraft((current) => ({ ...current, active: event.target.checked }))}
              />
              <span>
                <strong>Jetzt anzeigen</strong>
                <small>
                  {active && active.id !== draft.id
                    ? `Löst „${active.title}" ab – es ist immer nur eine Meldung aktiv.`
                    : 'Ohne Haken ist die Meldung gespeichert, aber niemand sieht sie.'}
                </small>
              </span>
            </label>

            {error ? <p className="admin-announce__error" role="alert">{error}</p> : null}

            <div className="admin-announce__actions">
              <button type="submit" className="admin-announce__save" disabled={busy || Boolean(problem)}>
                <Save size={13} /> {editing ? 'Änderungen speichern' : 'Meldung anlegen'}
              </button>
              {editing ? (
                <button type="button" className="admin-announce__ghost" onClick={() => { setDraft(emptyDraft); setError(null); }}>
                  <Plus size={13} /> Neue stattdessen
                </button>
              ) : null}
            </div>
          </form>

          <div className="admin-announce__preview">
            <span className="forq-eyebrow"><Eye size={11} /> So sehen es die Benutzer</span>
            {preview ? (
              <AnnouncementDialog
                embedded
                announcement={{
                  id: preview.id ?? 'vorschau',
                  title: preview.title.trim() || 'Überschrift',
                  body: preview.body.trim() || 'Text …',
                  tone: preview.tone,
                  repeat: preview.repeat,
                  updatedAt: new Date().toISOString(),
                }}
                note={preview.repeat === 'every-start' ? 'Dieser Hinweis erscheint bei jedem Start.' : undefined}
              />
            ) : (
              <p className="admin-empty">Sobald du etwas eintippst, erscheint hier die Vorschau.</p>
            )}
          </div>
        </div>
      ) : null}

      {snapshot.announcements.length > 0 ? (
        <>
          <h3 className="admin-announce__subhead">Angelegte Meldungen</h3>
          <ul className="admin-announce__list">
            {snapshot.announcements.map((entry) => (
              <li key={entry.id} className={`admin-announce__item${entry.active ? ' is-live' : ''}`}>
                <div className="admin-announce__item-main">
                  <strong>{entry.title}</strong>
                  <p>{entry.body}</p>
                  <small>
                    {entry.repeat === 'every-start' ? 'Bei jedem Start' : 'Einmalig'}
                    {' · '}zuletzt geändert {dateLabel(entry.updatedAt)}
                    {entry.updatedByName ? ` von ${entry.updatedByName}` : ''}
                  </small>
                </div>
                <div className="admin-announce__item-side">
                  <span className={`admin-announce__pill is-${entry.tone}${entry.active ? ' is-live' : ''}`}>
                    {entry.active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                  {canManage ? (
                    <div className="admin-announce__item-actions">
                      <button type="button" onClick={() => { setDraft(draftFrom(entry)); setError(null); }}>
                        Bearbeiten
                      </button>
                      <button
                        type="button"
                        className="admin-announce__delete"
                        disabled={busy}
                        onClick={() => void onDelete(entry.id)}
                      >
                        <Trash2 size={12} /> Löschen
                      </button>
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="admin-empty">Noch keine Systemmeldung angelegt.</p>
      )}
    </section>
  );
}
